import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { differenceInDays, differenceInHours, differenceInMinutes } from 'date-fns';
import {
  HrImageContainer,
  HrFlexContainer,
  HrTitleH2,
  HrIconText,
  HrFont,
  HrFontBold,
  HrFontExtraBold,
  HrParagraph,
  HrPrimary,
  HrSecondary,
  HrDarkGrey
} from '@components/styles/hr-styles';
import { DisplaySettingsContext } from '../store/display-settings';

const ContainerImageInfo = styled(HrFlexContainer)`
  @media (max-width: 425px) {
    align-items: center;
  }
`;

const ImageContainer = styled(HrImageContainer)`
  min-width: 112px;
  width: 112px;
  height: 100%;
  align-self: center;

  & img {
    height: 100%;
  }

  @media (max-width: 425px) {
    min-width: 72px;
    width: 72px;
  }
`;
const Tag = styled.div`
  background-color: ${(props) => props.backgroundColor || 'rgba(59, 63, 81, 0.1)'};
  color: ${(props) => props.color || HrSecondary} !important;
  margin: ${(props) => props.margin || '0'};
  padding: ${(props) => props.padding || '4px 16px'};
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 50px;
  font-family: ${HrFont};
  font-size: ${(props) => `${12 + (props.fontSizeSetting || 0)}px !important`};
  font-weight: ${(props) => props.fontWeight || HrFontBold};

  & .hr-Clock,
  & .hr-Remote,
  & .hr-Company {
    color: ${HrSecondary};
  }
`;

const FlexDesktop = styled(HrFlexContainer)`
  @media (max-width: 425px) {
    display: none;
  }
`;

const FlexMobile = styled(HrFlexContainer)`
  display: none;

  @media (max-width: 425px) {
    display: flex;
  }
`;

const IconText = styled(HrIconText)`
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  display: inline-block;
  font-size: ${(props) => `${14 + (props.fontSizeSetting || 0)}px !important`};

  & span {
    padding-right: 4px;
  }
`;

const FlexContainer = styled(HrFlexContainer)`
  flex-direction: column;
  align-items: flex-start;
  flex-wrap: nowrap;
  gap: 0;
  min-height: 112px;

  @media (max-width: 425px) {
    min-height: auto;
  }
`;

export const ApplicationInformation = ({ theme, maxWidth, vacancy, action }) => {
  const [t] = useTranslation();

  const workModality = (Modality) => {
    if (Modality === 1) {
      return 'Presencial';
    }

    if (Modality === 2) {
      return 'Remoto';
    }

    if (Modality === 3) {
      return 'Híbrido';
    }

    return 'No especificado';
  };

  const isNew = () => {
    const dateVacancy = vacancy.first_published_date || vacancy.created;
    const result = (Math.floor(new Date().getTime() / 1000.0) - dateVacancy) / 86400;
    return result <= 5;
  };

  const getDays = () => {
    const dateVacancy = vacancy.first_published_date || vacancy.created;
    // Cuantos Dias pasaron
    const resultInDays = differenceInDays(new Date(), new Date(dateVacancy * 1000));

    // Si es menos de un dia, retornamos Horas
    if (resultInDays <= 0) {
      const resultInHours = differenceInHours(new Date(), new Date(dateVacancy * 1000));

      // Si es una hora exacta, retorna un string especial
      if (resultInHours === 1) {
        return `Hace ${resultInHours} hora`;
      }

      // Si es menos de una Hora, retorna minutos
      if (resultInHours <= 0) {
        const resultInMinutes = differenceInMinutes(new Date(), new Date(dateVacancy * 1000));

        // // Si es un minuto exacto, retorna un string especial
        if (resultInMinutes <= 1) {
          return `Hace 1 minuto`;
        }

        return `Hace ${resultInMinutes} minutos`;
      }

      return `Hace ${resultInHours} horas`;
    }
    return `Hace ${resultInDays} días`;
  };

  const { darkMode, fontSizeSetting } = useContext(DisplaySettingsContext);
  return (
    <HrFlexContainer justifyContent='center' margin={action === 'edit' ? '-32px 0 0 0' : '-80px 0 0 0'}>
      <HrFlexContainer
        maxWidth={maxWidth}
        margin='0px 10px'
        padding='16px'
        backgroundColor={darkMode ? '#1A1D2B' : theme.secondaryColor}
      >
        {action !== 'edit' && (
          <HrFlexContainer>
            <ContainerImageInfo flexWrap='nowrap' alignItems={vacancy.status === 5 ? 'center' : 'flex-start'}>
              <ImageContainer>
                <img
                  src={
                    vacancy.imgLogo && vacancy.status !== 6 ? vacancy.imgLogo : '/assets/img/app/img-microsite/Logo.png'
                  }
                  alt='Logo de la empresa'
                />
              </ImageContainer>
              <FlexContainer justifyContent={vacancy.status === 5 ? 'center' : 'space-between'}>
                <HrFlexContainer>
                  <HrTitleH2
                    fontSizeSetting={fontSizeSetting}
                    color={darkMode ? 'white' : HrSecondary}
                    fontWeight={HrFontExtraBold}
                  >
                    {vacancy.name}{' '}
                    {vacancy.apto_discapacitados && (
                      <span
                        className='hr-Discapacity'
                        title={t(`crudVacancy.wizardForm.step2.inputLabel.aptoDiscapacitados`)}
                      />
                    )}
                  </HrTitleH2>
                </HrFlexContainer>
                <FlexDesktop flexDirection='column' gap='4px' alignItems='flex-start'>
                  {vacancy.status !== 5 && <LocalityArea vacancy={vacancy} />}
                </FlexDesktop>
                <FlexDesktop padding='8px 0 0 0' justifyContent='space-between'>
                  <HrFlexContainer width='auto' gap='4px'>
                    {vacancy.c_type_jobs && vacancy.c_type_jobs !== null && (
                      <Tag
                        fontSizeSetting={fontSizeSetting}
                        backgroundColor={darkMode ? '#3B3F51' : ''}
                        color={darkMode ? 'white' : ''}
                      >
                        <span
                          className='hr-Clock'
                          style={{ fontSize: `${12 + fontSizeSetting}px`, color: darkMode ? 'white' : '' }}
                        />
                        {vacancy.c_type_jobs === 'Intern' ? 'Pasantía' : vacancy.c_type_jobs}
                      </Tag>
                    )}
                    {vacancy.c_modality && (
                      <Tag
                        fontSizeSetting={fontSizeSetting}
                        backgroundColor={darkMode ? '#3B3F51' : ''}
                        color={darkMode ? 'white' : ''}
                      >
                        <span
                          style={{ fontSize: `${12 + fontSizeSetting}px`, color: darkMode ? 'white' : '' }}
                          className={vacancy.c_modality === 2 ? 'hr-Remote' : 'hr-Company'}
                        />
                        {workModality(vacancy.c_modality)}
                      </Tag>
                    )}
                  </HrFlexContainer>
                  {vacancy.status !== 5 && (
                    <HrFlexContainer gap='8px' justifyContent='flex-end' width='auto' flexWrap='nowrap'>
                      {isNew() && (
                        <HrParagraph
                          fontSizeSetting={fontSizeSetting}
                          style={{ whiteSpace: 'nowrap' }}
                          fontWeight={HrFontExtraBold}
                          color={HrPrimary}
                        >
                          Nuevo
                        </HrParagraph>
                      )}
                      <HrParagraph
                        fontSizeSetting={fontSizeSetting}
                        color={darkMode ? '#DADADA' : ''}
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        {getDays()}
                      </HrParagraph>
                    </HrFlexContainer>
                  )}
                </FlexDesktop>
                {vacancy.status === 5 && (
                  <HrParagraph fontSizeSetting={fontSizeSetting} color={darkMode ? 'white' : theme.textRegularColor}>
                    Completa tu CV para futuras búsquedas.
                  </HrParagraph>
                )}
              </FlexContainer>
            </ContainerImageInfo>
            <FlexMobile flexDirection='column' gap='4px' alignItems='flex-start'>
              {vacancy.status !== 5 && <LocalityArea vacancy={vacancy} />}
            </FlexMobile>
            <FlexMobile padding='8px 0 0 0' justifyContent='space-between'>
              <HrFlexContainer width='auto' gap='4px'>
                {vacancy.c_type_jobs && vacancy.c_type_jobs !== null && (
                  <Tag
                    fontSizeSetting={fontSizeSetting}
                    backgroundColor={darkMode ? '#3B3F51' : ''}
                    color={darkMode ? 'white' : ''}
                  >
                    <span
                      style={{ fontSize: `${12 + fontSizeSetting}px`, color: darkMode ? 'white' : '' }}
                      className='hr-Clock'
                    />
                    {vacancy.c_type_jobs === 'Intern' ? 'Pasantía' : vacancy.c_type_jobs}
                  </Tag>
                )}
                {vacancy.c_modality && (
                  <Tag
                    fontSizeSetting={fontSizeSetting}
                    backgroundColor={darkMode ? '#3B3F51' : ''}
                    color={darkMode ? 'white' : ''}
                  >
                    <span
                      style={{ fontSize: `${12 + fontSizeSetting}px`, color: darkMode ? 'white' : '' }}
                      className={vacancy.c_modality === 2 ? 'hr-Remote' : 'hr-Company'}
                    />
                    {workModality(vacancy.c_modality)}
                  </Tag>
                )}
              </HrFlexContainer>
              {vacancy.status !== 5 && (
                <HrFlexContainer gap='8px' justifyContent='flex-end' width='auto' flexWrap='nowrap'>
                  {isNew() && (
                    <HrParagraph
                      fontSizeSetting={fontSizeSetting}
                      style={{ whiteSpace: 'nowrap' }}
                      fontWeight={HrFontExtraBold}
                      color={HrPrimary}
                    >
                      Nuevo
                    </HrParagraph>
                  )}
                  <HrParagraph
                    fontSizeSetting={fontSizeSetting}
                    color={darkMode ? '#DADADA' : ''}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {getDays()}
                  </HrParagraph>
                </HrFlexContainer>
              )}
            </FlexMobile>
          </HrFlexContainer>
        )}
        {action === 'edit' && (
          <HrTitleH2
            fontSizeSetting={fontSizeSetting}
            color={darkMode ? 'white' : HrSecondary}
            fontWeight={HrFontExtraBold}
          >
            Actualiza los datos de tu perfil
          </HrTitleH2>
        )}
      </HrFlexContainer>
    </HrFlexContainer>
  );
};

export const LocalityArea = ({ vacancy }) => {
  const { darkMode, fontSizeSetting } = useContext(DisplaySettingsContext);
  const calculatedFontSize = 14 + fontSizeSetting;
  return (
    <>
      {vacancy.locality && (
        <IconText
          fontSizeSetting={fontSizeSetting}
          color={darkMode ? '#DADADA' : HrDarkGrey}
          cursor='default'
          title={vacancy.locality}
        >
          <span style={{ fontSize: `${calculatedFontSize}px` }} className='hr-Location-pin' />
          {vacancy.locality}
        </IconText>
      )}
      {vacancy.c_area && (
        <IconText
          fontSizeSetting={fontSizeSetting}
          color={darkMode ? '#DADADA' : HrDarkGrey}
          cursor='default'
          title={vacancy.c_area}
        >
          <span style={{ fontSize: `${calculatedFontSize}px` }} className='hr-Work-area' />
          {vacancy.c_area}
        </IconText>
      )}
    </>
  );
};
